import { ALLOWED_EXTENSIONS, FILE_KIND_HINTS, FILE_KIND_LABELS, FILE_KINDS, type CheckoutDto, type FileDto, type FileVersionDto, type LaunchCheckResultDto, type SubtaskDto, type UploadFileKind } from '@edms/shared';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type ChangeEvent, type DragEvent, type MouseEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Icon } from '../lib/icons';
import { printImage, printPdf } from '../lib/print';
import { formatDate, useToast } from '../lib/ui';
import { PdfView, TextView } from './pdf-view';

const ACCEPT = ALLOWED_EXTENSIONS.map((e) => `.${e}`).join(',');
const timeFmt = new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit' });

function size(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} КБ`;
  return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
}

function fileIcon(name: string): string {
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  if (['doc', 'docx', 'odt', 'rtf'].includes(ext)) return 'W';
  if (['xls', 'xlsx', 'ods', 'csv'].includes(ext)) return 'X';
  if (ext === 'pdf') return 'PDF';
  if (['png', 'jpg', 'jpeg'].includes(ext)) return 'IMG';
  return 'TXT';
}

function useFilesRefresh(taskId: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['task', taskId] });
  };
}

/** Скачать через ссылку: cookie отправится, файл сохранится браузером. */
function startDownload(fileId: string, version: number) {
  const a = document.createElement('a');
  a.href = `/api/files/${fileId}/download?version=${version}`;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/** Адрес просмотра файла: открывается и в новой вкладке (ПКМ → «Открыть в новой вкладке»). */
function fileHref(taskId: string, fileId: string, version: number): string {
  return `/tasks/${taskId}/files/${fileId}?version=${version}`;
}

/**
 * «Открыть» — ссылка: обычный щелчок показывает просмотр поверх задачи, а щелчок колёсиком,
 * с Ctrl/⌘ или «Открыть в новой вкладке» из меню открывает просмотр отдельной вкладкой.
 */
function ViewLink({ href, onView, className, title, children }: { href: string; onView: () => void; className: string; title?: string; children: ReactNode }) {
  const click = (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    onView();
  };
  return (
    <a href={href} className={className} title={title} onClick={click}>
      {children}
    </a>
  );
}

/** Просмотр файла отдельной вкладкой: «Закрыть» закрывает вкладку. */
export function FileViewPage() {
  const { id: taskId = '', fileId = '' } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const files = useQuery({ queryKey: ['task', taskId, 'files'], queryFn: () => api.get<FileDto[]>(`/tasks/${taskId}/files`) });
  const file = files.data?.find((f) => f.id === fileId);
  const version = Number(params.get('version')) || file?.currentVersion || 1;
  useEffect(() => {
    if (!file) return;
    const before = document.title;
    document.title = `${file.name} — EDMS`;
    return () => {
      document.title = before;
    };
  }, [file]);
  // Вкладку, открытую ссылкой, браузер разрешает закрыть; если нельзя (адрес открыли вручную) — к задаче.
  const close = () => {
    window.close();
    setTimeout(() => navigate(`/tasks/${taskId}`, { replace: true }), 200);
  };
  if (files.isPending) return <div className="empty"><span className="spinner" aria-hidden /> Открываем…</div>;
  if (!file) {
    return (
      <div className="empty">
        <b>Файл не найден</b>
        <span className="faint">Возможно, он удалён или у вас нет доступа к задаче.</span>
        <Link to={`/tasks/${taskId}`}>К задаче</Link>
      </div>
    );
  }
  return <Viewer file={file} version={version} onClose={close} />;
}

/** upload: 'all' — любые файлы, 'scan' — только сканы (после согласования), 'none' — задача закрыта. */
export function FilesBlock({
  taskId,
  upload,
  title = 'Файлы',
  nested,
  hideEmpty,
  problems = [],
  subtasks = [],
}: {
  taskId: string;
  upload: 'all' | 'scan' | 'none';
  title?: string;
  nested?: boolean;
  /** Не показывать блок, если файлов нет и загружать нельзя (подзадача, где от пользователя ничего не ждут). */
  hideEmpty?: boolean;
  /** Не пройденные проверки запуска: какого файла не хватает для согласования. */
  problems?: LaunchCheckResultDto[];
  /** Подзадачи: их файлы показываются в блоке основной задачи отдельными группами. */
  subtasks?: Array<Pick<SubtaskDto, 'id' | 'title' | 'assignee'>>;
}) {
  const files = useQuery({ queryKey: ['task', taskId, 'files'], queryFn: () => api.get<FileDto[]>(`/tasks/${taskId}/files`), refetchInterval: 30_000 });
  // Файлы хранятся у той подзадачи, где их приложили; здесь они собраны, чтобы всё было в одном месте.
  const subFiles = useQueries({ queries: subtasks.map((s) => ({ queryKey: ['task', s.id, 'files'], queryFn: () => api.get<FileDto[]>(`/tasks/${s.id}/files`) })) });
  const groups = subtasks.map((s, i) => ({ s, files: subFiles[i]?.data ?? [] })).filter((g) => g.files.length > 0);
  const [viewing, setViewing] = useState<{ file: FileDto; version: number } | null>(null);
  const [downloading, setDownloading] = useState<{ file: FileDto; taskId: string } | null>(null);
  if (hideEmpty && upload === 'none' && files.data?.length === 0) return null;

  const item = (owner: string, f: FileDto) => (
    <FileItem key={f.id} taskId={owner} file={f} onView={(version) => setViewing({ file: f, version })} onDownload={() => setDownloading({ file: f, taskId: owner })} />
  );
  return (
    <section className={nested ? 'files-nested' : 'card card-pad'}>
      <div className="section-head">
        {nested ? <b>{title}</b> : <h2>{title}</h2>}
        {!nested && <span className="faint">Правка — офлайн: скачали, поправили, загрузили новую версию</span>}
      </div>
      {files.data?.length === 0 && groups.length === 0 && <p className="faint">Файлов пока нет</p>}
      <ul className="files">{files.data?.map((f) => item(taskId, f))}</ul>
      {groups.map(({ s, files: list }) => (
        <div key={s.id} className="files-group">
          <div className="files-group-head">
            Из подзадачи «{s.title}»{s.assignee ? <span className="faint"> · {s.assignee.fullName}</span> : null}
          </div>
          <ul className="files">{list.map((f) => item(s.id, f))}</ul>
        </div>
      ))}
      {upload !== 'none' && <UploadNew taskId={taskId} scanOnly={upload === 'scan'} problems={problems} />}
      {viewing && <Viewer file={viewing.file} version={viewing.version} onClose={() => setViewing(null)} />}
      {downloading && <DownloadDialog taskId={downloading.taskId} file={downloading.file} onClose={() => setDownloading(null)} />}
    </section>
  );
}

function FileItem({ taskId, file, onView, onDownload }: { taskId: string; file: FileDto; onView: (v: number) => void; onDownload: () => void }) {
  const { user } = useAuth();
  const toast = useToast();
  const refresh = useFilesRefresh(taskId);
  const input = useRef<HTMLInputElement>(null);
  const [history, setHistory] = useState(false);
  const mine = file.editing.find((m) => m.user.id === user?.id);
  const others = file.editing.filter((m) => m.user.id !== user?.id);
  const current = file.versions[0];

  const [versionUpload, setVersionUpload] = useState<Upload | null>(null);
  const uploadVersion = useMutation({
    mutationFn: (f: File) => {
      const form = new FormData();
      // Версия, от которой шла правка: из отметки «редактирую» или текущая.
      form.set('basedOnVersion', String(mine?.baseVersion ?? file.currentVersion));
      form.set('file', f);
      const item: Upload = { id: ++uploadSeq, name: f.name, size: f.size, progress: 0, status: 'uploading' };
      setVersionUpload(item);
      return api.uploadWithProgress<{ version: number }>(`/files/${file.id}/versions`, form, (progress) => setVersionUpload({ ...item, progress }));
    },
    onSuccess: (r) => {
      setVersionUpload(null);
      toast(`Загружена версия ${r.version}`);
      refresh();
    },
    onError: (e) => {
      setVersionUpload(null);
      toast(e.message, { error: true });
    },
  });
  const changeKind = useMutation({
    mutationFn: (kind: UploadFileKind) => api.patch(`/files/${file.id}`, { kind }),
    onSuccess: () => {
      toast('Вид файла изменён');
      refresh();
    },
    onError: (e) => toast(e.message, { error: true }),
  });
  const cancel = useMutation({
    mutationFn: () => api.delete(`/files/${file.id}/editing`),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: () => api.delete(`/files/${file.id}`),
    onSuccess: () => {
      toast('Файл удалён');
      refresh();
    },
    onError: (e) => toast(e.message, { error: true }),
  });

  const pick = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (f) uploadVersion.mutate(f);
  };

  return (
    <li className="file">
      <div className="file-row">
        <span className={`file-icon fi-${fileIcon(file.name).toLowerCase()}`}>{fileIcon(file.name)}</span>
        <div className="file-info">
          <ViewLink href={fileHref(taskId, file.id, file.currentVersion)} onView={() => onView(file.currentVersion)} className="file-name" title="Открыть для чтения">
            {file.name}
          </ViewLink>
          <div className="faint">
            {file.canChangeKind ? (
              // Ошиблись с видом при загрузке — меняется здесь, без удаления и повторной загрузки.
              <select
                className="kind-select"
                value={file.kind}
                disabled={changeKind.isPending}
                onChange={(e) => changeKind.mutate(e.target.value as UploadFileKind)}
                aria-label={`Вид файла «${file.name}»`}
                title={`${kindHint(file.kind)}\n\nОшиблись при загрузке — выберите другой вид.`}
              >
                {FILE_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {FILE_KIND_LABELS[k]}
                  </option>
                ))}
              </select>
            ) : (
              <span title={kindHint(file.kind)}>{FILE_KIND_LABELS[file.kind]}</span>
            )}{' '}
            · версия {file.currentVersion}
            {current && ` · ${size(current.size)} · ${current.uploadedBy?.fullName ?? 'система'}, ${formatDate(current.createdAt)}`}
          </div>
        </div>
        <div className="file-actions">
          <ViewLink href={fileHref(taskId, file.id, file.currentVersion)} onView={() => onView(file.currentVersion)} className="btn btn-sm">
            Открыть
          </ViewLink>
          <button className="btn btn-sm" onClick={onDownload}>
            Скачать
          </button>
          {file.canUpload && (
            <>
              <button className="btn btn-sm btn-ghost" onClick={() => input.current?.click()} disabled={uploadVersion.isPending}>
                {uploadVersion.isPending ? 'Загружаем…' : 'Новая версия'}
              </button>
              <input ref={input} type="file" accept={ACCEPT} hidden onChange={pick} />
            </>
          )}
        </div>
      </div>
      {versionUpload && <UploadProgress items={[versionUpload]} />}
      {others.map((m) => (
        <div key={m.user.id} className="edit-banner">
          ✎ Редактирует {m.user.fullName} с {timeFmt.format(new Date(m.since))}
          {m.baseVersion < file.currentVersion && <span className="faint"> (правит устаревшую версию {m.baseVersion})</span>}
        </div>
      ))}
      {mine && (
        <div className="edit-banner mine">
          ✎ Вы правите версию {mine.baseVersion} с {timeFmt.format(new Date(mine.since))}.
          {mine.baseVersion < file.currentVersion ? (
            <b> Уже есть версия {file.currentVersion} — скачайте её и внесите правки заново.</b>
          ) : (
            ' Загрузите новую версию, когда закончите.'
          )}
          <button className="btn btn-sm btn-ghost" onClick={() => cancel.mutate()}>
            Отменить правку
          </button>
        </div>
      )}
      <div className="file-foot">
        {file.versions.length > 1 && (
          <button className="btn btn-ghost btn-sm" onClick={() => setHistory((h) => !h)}>
            {history ? '▾' : '▸'} История версий ({file.versions.length})
          </button>
        )}
        {file.canDelete && (
          <button className="btn btn-ghost btn-sm danger-text" onClick={() => window.confirm(`Удалить «${file.name}»?`) && remove.mutate()}>
            Удалить
          </button>
        )}
      </div>
      {history && (
        <ul className="versions">
          {file.versions.map((v) => (
            <VersionRow key={v.version} v={v} href={fileHref(taskId, file.id, v.version)} onView={() => onView(v.version)} onDownload={() => startDownload(file.id, v.version)} />
          ))}
        </ul>
      )}
    </li>
  );
}

function VersionRow({ v, href, onView, onDownload }: { v: FileVersionDto; href: string; onView: () => void; onDownload: () => void }) {
  return (
    <li>
      <b>v{v.version}</b>
      <span className="faint">
        {v.uploadedBy?.fullName ?? 'система'}, {formatDate(v.createdAt)}
        {v.basedOnVersion ? ` · правка версии ${v.basedOnVersion}` : ''}
      </span>
      {v.comment && <span>«{v.comment}»</span>}
      <span className="version-actions">
        <ViewLink href={href} onView={onView} className="btn btn-ghost btn-sm">
          Открыть
        </ViewLink>
        <button className="btn btn-ghost btn-sm" onClick={onDownload}>
          Скачать
        </button>
      </span>
    </li>
  );
}

/** «Вы будете править файл?» Закрыть без ответа — значит «только читаю». */
function DownloadDialog({ taskId, file, onClose }: { taskId: string; file: FileDto; onClose: () => void }) {
  const { user } = useAuth();
  const refresh = useFilesRefresh(taskId);
  const toast = useToast();
  const others = file.editing.filter((m) => m.user.id !== user?.id);
  const checkout = useMutation({
    mutationFn: (editing: boolean) => api.post<CheckoutDto>(`/files/${file.id}/checkout`, { editing }),
    onSuccess: (r, editing) => {
      startDownload(file.id, r.version);
      if (editing) {
        toast(r.othersEditing.length ? 'Внимание: файл правит ещё кто-то — договоритесь, чтобы не потерять правки' : 'Отметили, что вы правите файл');
        refresh();
      }
      onClose();
    },
    onError: (e) => toast(e.message, { error: true }),
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && checkout.mutate(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  return (
    <>
      <div className="drawer-backdrop" onClick={() => checkout.mutate(false)} />
      <div className="dialog" role="dialog" aria-modal="true" aria-labelledby="dl-title">
        <h2 id="dl-title">Вы будете править файл?</h2>
        <p className="muted">
          «{file.name}», версия {file.currentVersion}. Если будете править, коллеги увидят отметку до конца дня — так две правки не разойдутся.
        </p>
        {others.length > 0 && (
          <div className="alert warn">
            Сейчас файл правит {others.map((m) => `${m.user.fullName} (с ${timeFmt.format(new Date(m.since))})`).join(', ')}. Лучше договориться: кто загрузит
            версию позже, будет вносить правки заново.
          </div>
        )}
        <div className="decision-buttons">
          <button className="btn btn-primary" disabled={checkout.isPending} onClick={() => checkout.mutate(true)}>
            Да, редактирую
          </button>
          <button className="btn" disabled={checkout.isPending} onClick={() => checkout.mutate(false)}>
            Нет, только читаю
          </button>
        </div>
      </div>
    </>
  );
}

function Viewer({ file, version, onClose }: { file: FileDto; version: number; onClose: () => void }) {
  const v = file.versions.find((x) => x.version === version);
  const url = `/api/files/${file.id}/preview?version=${version}`;
  // Для PDF-копий Word/Excel опрашиваем, пока конвертация не закончится.
  const status = useQuery({
    queryKey: ['preview', file.id, version],
    queryFn: async () => {
      const res = await fetch(url, { method: 'HEAD', credentials: 'same-origin' });
      if (res.status === 202) return 'pending' as const;
      if (!res.ok) throw new ApiError(res.status, 'Просмотр этого файла недоступен — скачайте его');
      return 'ready' as const;
    },
    enabled: v?.preview !== 'native',
    refetchInterval: (q) => (q.state.data === 'pending' ? 3000 : false),
    retry: false,
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const ready = v?.preview === 'native' || status.data === 'ready';
  const isImage = v?.mime.startsWith('image/');
  const isText = v?.mime.startsWith('text/');
  const toast = useToast();
  const [printing, setPrinting] = useState(false);
  const print = async () => {
    setPrinting(true);
    try {
      await (isImage ? printImage(url) : printPdf(url));
    } catch (e) {
      toast(`Не удалось напечатать: ${(e as Error).message}. Скачайте файл и распечатайте его.`, { error: true });
    } finally {
      setPrinting(false);
    }
  };
  return (
    <div className="viewer" role="dialog" aria-modal="true" aria-label={`Просмотр: ${file.name}`}>
      <header>
        <b>{file.name}</b>
        <span className="faint">версия {version} · только чтение</span>
        <span style={{ flex: 1 }} />
        {ready && !isText && (
          <button className="btn btn-sm" onClick={() => void print()} disabled={printing} title="Печать страниц документа — как в просмотре">
            <Icon name="printer" size={16} /> {printing ? 'Готовим…' : 'Печать'}
          </button>
        )}
        <button className="btn btn-sm" onClick={() => startDownload(file.id, version)}>
          Скачать
        </button>
        <button className="btn btn-ghost btn-icon" onClick={onClose} aria-label="Закрыть">
          <Icon name="close" />
        </button>
      </header>
      <div className="viewer-body">
        {status.error && <div className="empty">{status.error.message}</div>}
        {!ready && !status.error && (
          <div className="empty">
            <span className="spinner" aria-hidden />
            Готовим просмотр…
          </div>
        )}
        {ready && (isImage ? <img src={url} alt={file.name} /> : isText ? <TextView url={url} /> : <PdfView url={url} />)}
      </div>
    </div>
  );
}

interface Upload {
  id: number;
  name: string;
  size: number;
  /** Доля отправленного, 0…1. */
  progress: number;
  status: 'queued' | 'uploading' | 'done' | 'error';
  error?: string;
}

let uploadSeq = 0;

/** Полоса загрузки: имя, размер, сколько отправлено, итог. */
export function UploadProgress({ items, onDismiss }: { items: Upload[]; onDismiss?: (id: number) => void }) {
  if (items.length === 0) return null;
  return (
    <ul className="upload-list" aria-live="polite">
      {items.map((u) => (
        <li key={u.id} className={`upload-item ${u.status}`}>
          <span className="upload-name" title={u.name}>
            {u.name}
          </span>
          <span className="faint upload-size">{size(u.size)}</span>
          <span className="upload-bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(u.progress * 100)} aria-label={`Загрузка ${u.name}`}>
            <span style={{ width: `${Math.round((u.status === 'done' ? 1 : u.progress) * 100)}%` }} />
          </span>
          <span className="upload-status">
            {u.status === 'queued' && 'в очереди'}
            {u.status === 'uploading' && `${Math.round(u.progress * 100)}%`}
            {u.status === 'done' && '✓'}
            {u.status === 'error' && (
              <>
                <span className="danger-text">{u.error}</span>
                {onDismiss && (
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => onDismiss(u.id)} aria-label="Убрать">
                    ×
                  </button>
                )}
              </>
            )}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** Подсказка к виду файла (для всплывающей подсказки): что это и чем вид отличается. */
function kindHint(kind: FileDto['kind']): string {
  const h = kind in FILE_KIND_HINTS ? FILE_KIND_HINTS[kind as UploadFileKind] : null;
  return h ? `${FILE_KIND_LABELS[kind]} — ${h.what}: ${h.examples}. ${h.effect[0]!.toUpperCase()}${h.effect.slice(1)}.` : FILE_KIND_LABELS[kind];
}

const KIND_ORDER: UploadFileKind[] = ['document', 'attachment', 'scan'];

/** Добавить файлы: несколько сразу (кнопкой или перетаскиванием), у каждого — полоса загрузки. */
function UploadNew({ taskId, scanOnly, problems }: { taskId: string; scanOnly: boolean; problems: LaunchCheckResultDto[] }) {
  const toast = useToast();
  const refresh = useFilesRefresh(taskId);
  const input = useRef<HTMLInputElement>(null);
  const [kind, setKind] = useState<UploadFileKind>(scanOnly ? 'scan' : 'document');
  const [guide, setGuide] = useState(false);
  const hint = FILE_KIND_HINTS[kind];
  const [drag, setDrag] = useState(false);
  const [items, setItems] = useState<Upload[]>([]);
  const busy = items.some((u) => u.status === 'queued' || u.status === 'uploading');
  const patch = (id: number, p: Partial<Upload>) => setItems((xs) => xs.map((x) => (x.id === id ? { ...x, ...p } : x)));

  // Файлы уходят по одному: так полоса честная, а сервер не получает десяток тяжёлых запросов разом.
  const start = async (files: File[]) => {
    if (files.length === 0) return;
    const batch = files.map((f) => ({ file: f, item: { id: ++uploadSeq, name: f.name, size: f.size, progress: 0, status: 'queued' as const } }));
    setItems((xs) => [...xs.filter((x) => x.status !== 'done'), ...batch.map((b) => b.item)]);
    let ok = 0;
    for (const { file, item } of batch) {
      patch(item.id, { status: 'uploading' });
      const form = new FormData();
      form.set('kind', kind);
      form.set('file', file);
      try {
        await api.uploadWithProgress(`/tasks/${taskId}/files`, form, (progress) => patch(item.id, { progress }));
        patch(item.id, { status: 'done', progress: 1 });
        ok += 1;
        refresh();
      } catch (e) {
        patch(item.id, { status: 'error', error: (e as Error).message });
      }
    }
    if (ok) toast(ok === 1 ? 'Файл добавлен' : `Добавлено файлов: ${ok}`);
    // Готовые строки исчезают сами, ошибки остаются, пока их не закроют.
    const doneIds = new Set(batch.map((b) => b.item.id));
    setTimeout(() => setItems((xs) => xs.filter((x) => !(doneIds.has(x.id) && x.status === 'done'))), 2500);
  };

  const drop = (e: DragEvent) => {
    e.preventDefault();
    setDrag(false);
    void start(Array.from(e.dataTransfer.files));
  };
  return (
    <div className="upload">
      {/* Маршрут требует файл определённого вида — подсказка и кнопка, которая сразу выбирает этот вид. */}
      {problems.map((c) => {
        if (c.target?.type !== 'file') return null;
        const need = c.target.kind as UploadFileKind;
        return (
          <div key={c.label} className="alert warn files-problem" data-check-target={`file:${need}`}>
            <span>
              <b>{c.label}</b> — нужен файл вида «{FILE_KIND_LABELS[need]}», без него согласование не запустить. Загрузите его или смените вид у уже загруженного файла.
            </span>
            {(need === 'scan' || !scanOnly) && (
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => {
                  setKind(need);
                  input.current?.click();
                }}
              >
                <Icon name="plus" size={16} /> Загрузить как «{FILE_KIND_LABELS[need]}»
              </button>
            )}
          </div>
        );
      })}
      <div
        className={`dropzone${drag ? ' over' : ''}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={drop}
      >
        <select className="select" value={kind} onChange={(e) => setKind(e.target.value as UploadFileKind)} aria-label="Вид файла">
          {!scanOnly && <option value="document">Документ</option>}
          {!scanOnly && <option value="attachment">Вложение</option>}
          <option value="scan">Скан подписанного</option>
        </select>
        <button className="btn" onClick={() => input.current?.click()}>
          <Icon name="plus" size={16} /> {busy ? 'Добавить ещё' : 'Добавить файлы'}
        </button>
        <span className="faint">или перетащите сюда несколько сразу · PDF, Word, Excel, изображения, до 25 МБ каждый</span>
        <input
          ref={input}
          type="file"
          accept={ACCEPT}
          multiple
          hidden
          onChange={(e) => {
            const files = Array.from(e.target.files ?? []);
            e.target.value = '';
            void start(files);
          }}
        />
        <p className="kind-hint">
          <b>{FILE_KIND_LABELS[kind]}</b> — {hint.what}: {hint.examples}.{' '}
          {!scanOnly && (
            <button type="button" className="link-btn" onClick={() => setGuide((v) => !v)} aria-expanded={guide}>
              {guide ? 'Скрыть' : 'Какой вид выбрать?'}
            </button>
          )}
        </p>
        {guide && (
          <dl className="kind-guide">
            {KIND_ORDER.map((k) => (
              <div key={k} className={k === kind ? 'active' : undefined}>
                <dt>{FILE_KIND_LABELS[k]}</dt>
                <dd>
                  <b>{FILE_KIND_HINTS[k].what[0]!.toUpperCase() + FILE_KIND_HINTS[k].what.slice(1)}.</b> Например: {FILE_KIND_HINTS[k].examples}.
                  <span className="faint"> {FILE_KIND_HINTS[k].effect[0]!.toUpperCase() + FILE_KIND_HINTS[k].effect.slice(1)}.</span>
                </dd>
              </div>
            ))}
            <p className="faint">Ошиблись — вид можно сменить у файла в списке выше, пока документ не согласован.</p>
          </dl>
        )}
      </div>
      <UploadProgress items={items} onDismiss={(id) => setItems((xs) => xs.filter((x) => x.id !== id))} />
    </div>
  );
}
